import logger from '@/logger';
import {
  acertoPushPermissaoConcedida,
  acertoPushPermissaoNegada,
  acertoPushPermissaoRevogada,
} from '@/modules/events/acerto';
import pushSubscriptionService from '@/services/PushSubscriptionService';
import sessionService from '@/services/SessionService';
import storageService from '@/services/StorageService';
import { calcularJaInstalado } from '@shared/pwa/utils/instalacaoPwa';
import { detectarPlataformaPwa } from '@shared/pwa/utils/plataformaPwa';
import { pwaHabilitado } from '@shared/pwa/utils/pwaHabilitado';
import { registrarServiceWorker } from '@shared/pwa/utils/registroServiceWorker';

function suportaPush(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/** Nunca lança: se falhar, `pedirPermissaoEAssinarPush` busca a chave de novo no gesto. */
export async function precarregarChavePublicaVapid(): Promise<void> {
  if (!suportaPush() || Notification.permission === 'denied') return;

  try {
    await pushSubscriptionService.obterChavePublicaVapid();
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

/** `default` é o pop-up fechado sem escolha: o usuário ainda pode decidir depois, então não conta. */
function registrarRespostaDaPermissao(resultado: NotificationPermission) {
  if (resultado === 'granted') acertoPushPermissaoConcedida(detectarPlataformaPwa());
  if (resultado === 'denied') acertoPushPermissaoNegada(detectarPlataformaPwa());
}

function mesmaChave(chaveDaAssinatura: ArrayBuffer | null | undefined, chaveVapid: Uint8Array): boolean {
  if (!chaveDaAssinatura) return true;
  const bytes = new Uint8Array(chaveDaAssinatura);
  return bytes.length === chaveVapid.length && bytes.every((byte, indice) => byte === chaveVapid[indice]);
}

/**
 * `instalada` entra na comparação porque muda depois da assinatura: no Android quem assinou no
 * navegador e instala depois divide o armazenamento com o app, e o backend precisa do upsert com
 * `true` para contar o consumidor como instalado. O dono entra porque a sessão pode expirar com o
 * app fechado: o logout do boot não tem token em memória e não remove a assinatura, e sem o reenvio
 * o backend continuaria entregando ao usuário anterior os avisos de quem logou depois.
 */
function assinaturaJaRegistrada(endpoint: string, instalada: boolean, usuarioHash: string | undefined): boolean {
  const registrada = storageService.pushAssinatura.get();
  return (
    registrada?.endpoint === endpoint && registrada.instalada === instalada && registrada.usuarioHash === usuarioHash
  );
}

function chavesDaAssinatura(assinatura: PushSubscription): { p256dh: string; auth: string } {
  const { p256dh, auth } = assinatura.toJSON().keys ?? {};
  if (!p256dh || !auth) throw new Error('Assinatura de push sem as chaves p256dh/auth');
  return { p256dh, auth };
}

/**
 * Teto para o SW recém-registrado ativar. Sem ele, um SW que falha na instalação deixaria o
 * `ready` pendente para sempre e travaria a fila de `enfileirar`, inclusive a remoção no logout.
 */
const ESPERA_MAXIMA_SW_ATIVO_MS = 10_000;

/**
 * No primeiro acesso o `register()` devolve o registro com o SW ainda instalando, e o `subscribe`
 * é rejeitado sem um SW ativo. É mais provável no iOS, onde o toque em "Ativar" pode vir logo na
 * primeira abertura do app.
 */
async function aguardarServiceWorkerAtivo(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active) return;

  let temporizador: ReturnType<typeof setTimeout> | undefined;
  const esgotou = new Promise<never>((_resolve, reject) => {
    temporizador = setTimeout(
      () => reject(new Error('O service worker não ativou a tempo para assinar o push')),
      ESPERA_MAXIMA_SW_ATIVO_MS
    );
  });

  try {
    await Promise.race([navigator.serviceWorker.ready, esgotou]);
  } finally {
    clearTimeout(temporizador);
  }
}

/**
 * Uma assinatura criada com outra chave VAPID (rotação de chave) não recebe mais push do
 * backend, e uma expirada é rejeitada pelo serviço de push do navegador.
 */
function assinaturaValida(assinatura: PushSubscription, chaveVapid: Uint8Array): boolean {
  const expirou = assinatura.expirationTime != null && assinatura.expirationTime <= Date.now();
  return !expirou && mesmaChave(assinatura.options?.applicationServerKey, chaveVapid);
}

/**
 * Pede permissão de notificação, cria a assinatura de push e a envia ao backend. Deve ser
 * chamada a partir de uma ação do usuário (o iOS exige), por isso não passa pela fila de
 * `enfileirar`: a espera tiraria o pedido de dentro do gesto. Idempotente: não pede de novo se a
 * permissão já foi decidida (`Notification.permission !== 'default'`), e não recria nem
 * reenvia uma assinatura válida que o backend já confirmou. Uma assinatura que existe só no
 * navegador (envio anterior falhou, ou o navegador a trocou) ou confirmada em nome de outro usuário é
 * reenviada, e uma inválida (expirada ou de outra chave
 * VAPID) é recriada. Fora do tema Acerto (ver `pwaHabilitado`) não faz nada. Nunca lança: navegador sem suporte, permissão negada e
 * falha de rede são tratados como no-op. A chave VAPID só é buscada depois da permissão, para o
 * `requestPermission` sair ainda dentro do gesto; `precarregarChavePublicaVapid` adianta a busca.
 */
export async function pedirPermissaoEAssinarPush(): Promise<void> {
  if (!suportaPush() || !pwaHabilitado()) return;

  try {
    let permissao = Notification.permission;
    if (permissao === 'denied') return;

    if (permissao === 'default') {
      permissao = await Notification.requestPermission();
      registrarRespostaDaPermissao(permissao);
      if (permissao !== 'granted') return;
    }

    const { chavePublica } = await pushSubscriptionService.obterChavePublicaVapid();
    const chaveVapid = urlBase64ToUint8Array(chavePublica);

    const registration = await registrarServiceWorker();
    if (!registration) return;
    await aguardarServiceWorkerAtivo(registration);

    let assinatura = await registration.pushManager.getSubscription();

    if (assinatura && !assinaturaValida(assinatura, chaveVapid)) {
      await assinatura.unsubscribe();
      assinatura = null;
    }

    const instalada = calcularJaInstalado();
    const usuarioHash = sessionService.token?.payload.hash;
    if (assinatura && assinaturaJaRegistrada(assinatura.endpoint, instalada, usuarioHash)) return;

    assinatura ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chaveVapid,
    });

    const keys = chavesDaAssinatura(assinatura);
    await pushSubscriptionService.registrar({
      endpoint: assinatura.endpoint,
      keys,
      plataforma: detectarPlataformaPwa(),
      instalada,
    });
    storageService.pushAssinatura.set({ endpoint: assinatura.endpoint, auth: keys.auth, instalada, usuarioHash });
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Remove a assinatura de push no backend e, só depois da confirmação, no navegador. Se o
 * backend falhar, a assinatura local é mantida para não deixar no backend um registro que o
 * front não consegue mais identificar. O registro guardado é sempre limpo: o próximo usuário do
 * dispositivo reenvia a assinatura em seu nome ao logar. As notificações já exibidas são fechadas.
 * Chamada no logout e na troca de usuário
 * — nunca lança, para não travar esses fluxos.
 */
export async function removerAssinaturaPush(): Promise<void> {
  if (!suportaPush()) return;

  await enfileirar(removerNoBackendENoNavegador);
}

/**
 * Chamada ao abrir o app. Com a permissão bloqueada, o navegador para de entregar push sem avisar o
 * backend, e o `getSubscription()` pode não devolver mais a assinatura, por isso a remoção usa o
 * `endpoint` e o `auth` guardados. Uma falha mantém o registro para tentar de novo na próxima
 * abertura. Vale em qualquer tema e sem sessão: a rota é anônima. O `permission_revoked` sai junto com a
 * limpeza do registro, e não na detecção, para não se repetir a cada abertura enquanto o backend falhar.
 * Nunca lança.
 */
export async function removerAssinaturaComPermissaoRevogada(): Promise<void> {
  if (!('Notification' in window) || Notification.permission === 'granted') return;

  await enfileirar(async () => {
    const registrada = storageService.pushAssinatura.get();
    if (!registrada) return;

    try {
      await pushSubscriptionService.remover({
        endpoint: registrada.endpoint,
        auth: registrada.auth,
        motivo: 'permissao_revogada',
      });
      storageService.pushAssinatura.remove();
      acertoPushPermissaoRevogada(detectarPlataformaPwa());
    } catch (error) {
      logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

/**
 * Recria a assinatura sem mostrar convite quando a permissão já foi concedida antes. A
 * permissão é do navegador, não do usuário, e o logout remove só a assinatura: sem isso,
 * depois de um logout ninguém volta a receber push no dispositivo, porque o convite de
 * instalação não aparece mais com o PWA já instalado ou dispensado.
 */
export async function reassinarPushSilenciosamente(): Promise<void> {
  if (!suportaPush() || Notification.permission !== 'granted') return;

  await enfileirar(pedirPermissaoEAssinarPush);
}

let filaDeAssinatura: Promise<void> = Promise.resolve();

const TRAVA_ASSINATURA = 'acerto-pwa-assinatura-push';

/**
 * A fila em memória só ordena a própria aba; a trava do navegador ordena as abas e janelas do app
 * entre si, que dividem a mesma assinatura. Sem a Web Locks API (Safari < 15.4), vale só a fila.
 */
async function comTravaEntreAbas(operacao: () => Promise<void>): Promise<void> {
  if (!('locks' in navigator)) return operacao();

  try {
    await navigator.locks.request(TRAVA_ASSINATURA, operacao);
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Remoção e reassinatura mexem na mesma assinatura do navegador. Na troca de usuário as duas
 * disparam juntas, e sem ordem a reassinatura do novo usuário reenviaria a assinatura que a
 * remoção do anterior está apagando. As operações enfileiradas nunca rejeitam.
 */
function enfileirar(operacao: () => Promise<void>): Promise<void> {
  filaDeAssinatura = filaDeAssinatura.then(() => comTravaEntreAbas(operacao));
  return filaDeAssinatura;
}

/**
 * As notificações de quem saiu continuam na bandeja depois do logout, e o próximo usuário do
 * aparelho poderia lê-las ou tocá-las. Uma falha aqui não impede a remoção da assinatura.
 */
async function fecharNotificacoesExibidas(registration: ServiceWorkerRegistration | undefined): Promise<void> {
  if (!registration) return;

  try {
    const notificacoes = await registration.getNotifications();
    for (const notificacao of notificacoes) notificacao.close();
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * O registro guardado tem prioridade sobre a assinatura do navegador porque é o que o backend
 * confirmou; se o navegador trocou a assinatura sem reenvio, a do navegador nunca chegou lá.
 */
async function removerNoBackendENoNavegador(): Promise<void> {
  try {
    const registrada = storageService.pushAssinatura.get();
    storageService.pushAssinatura.remove();

    const registration = await navigator.serviceWorker.getRegistration();
    await fecharNotificacoesExibidas(registration);
    const subscription = await registration?.pushManager.getSubscription();
    const alvo =
      registrada ?? (subscription && { endpoint: subscription.endpoint, ...chavesDaAssinatura(subscription) });
    if (!alvo) return;

    await pushSubscriptionService.remover({ endpoint: alvo.endpoint, auth: alvo.auth, motivo: 'revogada' });
    await subscription?.unsubscribe();
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}
